/**
 * A pace typed in setup or on You, in the person's unit per week. The usual choices stay as they
 * are; a number of your own is saved the same way, up to the ceiling for that goal
 * (shared/targets.ts). Losing can pass 2 lb a week (about 0.9 kg), with a note. Gaining stops at ½ kg.
 */
import { parseDecimal } from '../../shared/numbers';
import { PACE_CAUTION_KG, paceCautionNote, paceCeilingKg, type PaceGoal } from '../../shared/targets';
import { formatWeight, KG_PER_LB, type WeightUnit } from '../../shared/units';

/** What's typed, in kg per week: null when blank, "invalid" when it isn't a positive number. */
export function parsePaceInput(text: string | undefined, unit: WeightUnit): number | null | 'invalid' {
  if (!text?.trim()) return null;
  const n = parseDecimal(text);
  if (n === null || n <= 0) return 'invalid';
  const kg = unit === 'lb' ? n * KG_PER_LB : n;
  return Number.isFinite(kg) ? kg : 'invalid';
}

/** A pace written back into the field after a unit change, so 1.25 lb comes back as 1.25. */
export function formatPaceInput(paceKg: number, unit: WeightUnit): string {
  const shown = unit === 'lb' ? paceKg / KG_PER_LB : paceKg;
  const digits = unit === 'lb' ? 2 : 3;
  return String(Math.round(shown * 10 ** digits) / 10 ** digits);
}

/** Why a typed pace can't be saved, or null when it's blank (a preset is in use) or fine. */
export function paceInputError(text: string | undefined, unit: WeightUnit, direction: PaceGoal = 'lose'): string | null {
  const parsed = parsePaceInput(text, unit);
  if (parsed === null || parsed === 'invalid') return unit === 'lb' ? 'Enter a pace above 0, like 1.' : 'Enter a pace above 0, like 0.5.';
  const ceiling = paceCeilingKg(direction);
  if (parsed > ceiling + 1e-9) return `Enter a pace up to ${formatWeight(ceiling, unit)} a week.`;
  return null;
}

/** The note for a pace past 2 lb a week, or null when there isn't one (including when the pace is refused). */
export function paceInputCaution(text: string | undefined, unit: WeightUnit, direction: PaceGoal = 'lose'): string | null {
  const parsed = parsePaceInput(text, unit);
  const ceiling = paceCeilingKg(direction);
  if (typeof parsed !== 'number' || parsed > ceiling + 1e-9 || parsed <= PACE_CAUTION_KG + 1e-9) return null;
  return paceCautionNote(unit);
}
